import { Inject, Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { ContextService } from '../../shared/shared-context/context.service';
import { SERVICE_WORKER_ENABLED } from '../service-worker-enabled.injection-token';
import { ServiceWorkerMessage } from '../service-worker-message.model';
import { ServiceWorkerRegistrationHandler } from './service-worker-registration-handlers.injection-token';

// Keep in sync with `shell-and-config.warmup.ts`and `shared-inline.ts`. We should bump the version if there are major changes in data contract, since
// an installing service worker might not have the same data contract as the app
interface AppLoadMessagePayloadV1 {
    success: boolean; // Whether the  app loaded successfully
    unregister: boolean; // If unsuccessful, whether this failure should result in unregistering the active worker during new installation
}

interface AppLoadMessage extends ServiceWorkerMessage {
    type: 'OFFICECOM_LOAD_COMPLETE';
    meta: 'installation-app';
    payload: AppLoadMessagePayloadV1;
}

/**
 * Responsible for informing Worker that app has loaded so it can complete installation
 */
@Injectable()
export class ServiceWorkerAppLoadService implements ServiceWorkerRegistrationHandler {

    constructor (
        private readonly errorHandler: OfficeHomePageErrorHandler,
        private readonly contextService: ContextService,
        @Inject(SERVICE_WORKER_ENABLED) private readonly serviceWorkerEnabled: boolean
    ) {
    }

    public async handleRegistration(registration: ServiceWorkerRegistration): Promise<void> {
        if (!this.serviceWorkerEnabled) {
            return;
        }

        try {
            const serviceWorkerIntervalUpdateEnabled = this.contextService.sharedContext.serviceWorkerIntervalUpdateEnabled;
            const postMessageOnUpdate = () => {
                registration.addEventListener('updatefound', () => {
                    this.postAppLoadedMessage(registration.installing);
                });
            };

            if (registration.installing) {
                this.postAppLoadedMessage(registration.installing);
                if (serviceWorkerIntervalUpdateEnabled) {
                    postMessageOnUpdate();
                }
            } else {
                postMessageOnUpdate();
            }
        } catch (ex) {
            this.errorHandler.handleError(ex, ErrorId.GDHPG);
        }
    }

    private postAppLoadedMessage(installingWorker: ServiceWorker) {
        const payload: AppLoadMessagePayloadV1 = {
            success: true,
            unregister: false
        };

        const message: AppLoadMessage = {
            type: 'OFFICECOM_LOAD_COMPLETE',
            meta: 'installation-app',
            payload
        };

        installingWorker.postMessage(message);
    }

}
